/**
 * Powdercoat surcharge — cart recalc module.
 *
 * Adds/removes a powdercoat-toeslag line item per unique RAL color of
 * poedercoat-flagged variants in the cart. Drops the surcharge for a color once
 * the cart contains >= 35m of that color.
 *
 * Reads cart state from a server-rendered manifest (sections/powdercoat-manifest.liquid),
 * which is re-fetched after each cart change to capture data that /cart.js doesn't
 * expose: the rts.poedercoattoeslag flag and the strekkende meters per unit (from
 * the metaobject linked to the "Keuze" variant option).
 */
(function () {
  'use strict';

  var MANIFEST_SCRIPT_ID = 'powdercoat-cart-manifest';
  var MANIFEST_SECTION_ID = 'powdercoat-manifest';
  var INFO_BOX_SELECTOR = '[data-powdercoat-info]';

  /* ---------- Helpers ---------- */

  function parseManifestFromDom() {
    var el = document.getElementById(MANIFEST_SCRIPT_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[powdercoat] failed to parse manifest from DOM', e);
      return null;
    }
  }

  /* Color key for grouping = the raw "Afwerking" option value (e.g.
     "Antraciet (RAL 7016)"). It's driven by the linked shopify.color-pattern
     metaobject, so the same color uses an identical label across products —
     a stable cross-product key, no RAL parsing needed. */
  function colorKey(afwerking) {
    return afwerking || 'UNKNOWN';
  }

  function aggregateTotals(items) {
    var totals = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var key = colorKey(item.afwerking);
      var profileLength = parseFloat(item.meters_per_unit);
      if (!isFinite(profileLength) || profileLength <= 0) {
        console.warn('[powdercoat] surcharge variant ' + item.variant_id +
          ' has invalid/missing meters_per_unit (' + item.meters_per_unit +
          ') — treating as 0m (surcharge will apply)');
        profileLength = 0;
      }
      var meters = profileLength * Number(item.quantity);
      totals[key] = (totals[key] || 0) + meters;
    }
    return totals;
  }

  /* ---------- Surcharge engine ---------- */

  function PowdercoatSurcharge() {
    this.manifest = parseManifestFromDom();
    this.recalcLock = false;
    this.pendingRecalc = false;
    this.bindEvents();
    /* First pass: cart may contain stale surcharges from a prior session,
       or admin may have changed something. Run a recalc on boot. */
    this.scheduleRecalc();
  }

  PowdercoatSurcharge.prototype.bindEvents = function () {
    var self = this;
    /* product:added bubbles up from buy-button (events.md: cart-flow). */
    document.addEventListener('product:added', function () {
      self.scheduleRecalc();
    });
    /* cart:rerendered is dispatched on Cart.element. On cart page it bubbles;
       on mini-cart it does NOT bubble (documented bug in events.md). A single
       capture-phase listener on `document` covers both: capture descends from
       root to target before the event reaches Cart.element, so it fires
       regardless of bubbling. */
    document.addEventListener('cart:rerendered', function () {
      self.scheduleRecalc();
    }, true);
  };

  PowdercoatSurcharge.prototype.scheduleRecalc = function () {
    var self = this;
    if (this.recalcLock) {
      this.pendingRecalc = true;
      return;
    }
    this.recalcLock = true;
    (function loop() {
      self.pendingRecalc = false;
      self.runRecalc().catch(function (e) {
        console.error('[powdercoat] recalc failed', e);
      }).then(function () {
        if (self.pendingRecalc) {
          loop();
        } else {
          self.recalcLock = false;
        }
      });
    })();
  };

  PowdercoatSurcharge.prototype.runRecalc = function () {
    var self = this;
    return this.refetchManifest().then(function () {
      if (!self.manifest) return;
      return self.applyDiff();
    });
  };

  PowdercoatSurcharge.prototype.refetchManifest = function () {
    var self = this;
    var base = (window.routes && window.routes.rootUrlWithoutSlash) || '';
    var url = base + '/?section_id=' + MANIFEST_SECTION_ID;
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('manifest fetch HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var script = tmp.querySelector('#' + MANIFEST_SCRIPT_ID);
        if (!script) throw new Error('manifest script missing in section HTML');
        var parsed = JSON.parse(script.textContent);
        self.manifest = parsed;
        /* Mirror into the live DOM so any consumer reading the static script
           tag (e.g. PDP info-box on a subsequent render) gets fresh data. */
        var live = document.getElementById(MANIFEST_SCRIPT_ID);
        if (live) live.textContent = script.textContent;
      })
      .catch(function (e) {
        console.warn('[powdercoat] refetch failed, keeping stale manifest', e);
      });
  };

  PowdercoatSurcharge.prototype.applyDiff = function () {
    var m = this.manifest;
    if (m.surcharge_available === false || !m.surcharge_variant_id) {
      console.warn('[powdercoat] surcharge product not available via storefront ' +
        '(check Online Store publication for handle "poedercoat-toeslag") — ' +
        'skipping cart mutations');
      return Promise.resolve();
    }
    var totals = aggregateTotals(m.made_to_order_items);

    /* Needed: 1 surcharge per color where total < threshold (strict). */
    var needed = {};
    var color;
    for (color in totals) {
      if (totals[color] < m.threshold_meters) needed[color] = true;
    }

    /* Present: existing surcharge line items grouped by color. */
    var present = {};
    for (var i = 0; i < m.surcharge_items.length; i++) {
      var item = m.surcharge_items[i];
      var k = item.for_color || 'UNKNOWN';
      /* Multiple surcharge items for same color = duplicates from prior bugs.
         Mark all but first for removal. */
      if (present[k]) {
        present[k].duplicates.push(item);
      } else {
        present[k] = { primary: item, duplicates: [] };
      }
    }

    var toAdd = [];
    var toRemove = [];

    /* Adds: needed colors without an existing primary surcharge. */
    for (color in needed) {
      if (!present[color]) toAdd.push(color);
    }

    /* Removes: present colors no longer needed, OR primary with quantity != 1
       (normalize to 1), OR duplicates. */
    for (color in present) {
      var entry = present[color];
      for (var d = 0; d < entry.duplicates.length; d++) {
        toRemove.push(entry.duplicates[d]);
      }
      if (!needed[color]) {
        toRemove.push(entry.primary);
      } else if (entry.primary.quantity !== 1) {
        toRemove.push(entry.primary);
        toAdd.push(color);
      }
    }

    /* Notify PDP info-box (and anything else) regardless of whether we mutate
       the cart, so the meter readout updates on quantity changes that don't
       cross the threshold. */
    var activeColors = [];
    for (color in needed) activeColors.push(color);
    document.dispatchEvent(new CustomEvent('powdercoat:recalculated', {
      detail: {
        totalsByColor: totals,
        surchargesActive: activeColors,
        thresholdMeters: m.threshold_meters,
        surchargePriceFormatted: m.surcharge_price_formatted
      }
    }));

    if (toAdd.length === 0 && toRemove.length === 0) return Promise.resolve();

    var self = this;
    var ops = Promise.resolve();

    if (toRemove.length > 0) {
      ops = ops.then(function () {
        var updates = {};
        for (var r = 0; r < toRemove.length; r++) updates[toRemove[r].key] = 0;
        /* `{ updates: {...} }` payload requires /cart/update.js — the singular
           /cart/change.js endpoint takes a different shape ({ line, quantity }) */
        var cartBase = (window.routes && window.routes.cartUrl) || '/cart';
        var updateUrl = cartBase + '/update.js';
        return fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates }),
          credentials: 'same-origin'
        }).then(function (res) {
          if (!res.ok) throw new Error('cart/update HTTP ' + res.status);
        });
      });
    }

    if (toAdd.length > 0) {
      ops = ops.then(function () {
        var items = toAdd.map(function (c) {
          return {
            id: m.surcharge_variant_id,
            quantity: 1,
            properties: {
              _powdercoat_for_color: c,
              'Kleur': c
            }
          };
        });
        var addUrl = ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';
        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items }),
          credentials: 'same-origin'
        }).then(function (res) {
          if (!res.ok) throw new Error('cart/add HTTP ' + res.status);
        });
      });
    }

    return ops.then(function () {
      /* Trigger Cart UI refresh (mini-cart + cart page) without opening the
         drawer (cart:refresh, not product:added). */
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      }));
    });
  };

  /* ---------- PDP info box ----------
     Stateless controller: queries DOM on every render. Necessary because the
     variant-selector block (containing the info-box) is `replaceWith`'d by
     ProductRerender on variant change — any cached element references would
     point to a detached node. The fresh server-rendered Liquid block carries
     correct data-attributes for the new variant, so we just re-read them. */

  function InfoBoxController() {
    var self = this;
    this._isInitialRender = true;
    document.addEventListener('variant:change', function () {
      /* variant:change fires BEFORE product:rerender swaps the variant-selector
         block. Defer to next task so we read the post-swap DOM. */
      setTimeout(function () { self.render(); }, 0);
    });
    document.addEventListener('powdercoat:recalculated', function () {
      self.render();
    });
    this.render();
  }

  InfoBoxController.prototype.render = function () {
    var element = document.querySelector(INFO_BOX_SELECTOR);
    if (!element || element.hidden) return;

    var threshold = parseFloat(element.getAttribute('data-threshold-m')) || 35;
    var surchargePrice = element.getAttribute('data-surcharge-price') || '';
    var currentAfwerking = element.getAttribute('data-current-afwerking') || '';
    var currentColorKey = colorKey(currentAfwerking);

    var msgEl = element.querySelector('[data-powdercoat-msg]');
    var barWrapEl = element.querySelector('[data-powdercoat-bar-wrap]');
    var barFillEl = element.querySelector('[data-powdercoat-bar]');
    if (!msgEl || !barWrapEl || !barFillEl) return;

    var manifest = parseManifestFromDom();
    var totals = manifest ? aggregateTotals(manifest.made_to_order_items) : {};
    var total = totals[currentColorKey] || 0;

    var totalDisplay = (Math.round(total * 10) / 10).toString().replace(/\.0$/, '');
    var remaining = Math.max(0, threshold - total);
    var remainingDisplay = (Math.round(remaining * 10) / 10).toString().replace(/\.0$/, '');
    var pct = Math.min(100, (total / threshold) * 100);
    var colorLabel = currentAfwerking || currentColorKey;

    var state;
    if (total >= threshold) state = 'free';
    else if (total > 0) state = 'progress';
    else state = 'idle';

    /* Skip DOM rewrite alleen bij de INITIAL render én als liquid al de juiste
       state had — voorkomt onnodige innerHTML-overschrijvingen bij page-load.
       Event-driven renders (variant:change, powdercoat:recalculated) draaien
       altijd door, ook al wijzigt state niet (de bar-width/totalen kunnen wel
       wijzigen binnen dezelfde state). */
    if (this._isInitialRender) {
      this._isInitialRender = false;
      if (element.getAttribute('data-state') === state) return;
    }

    element.setAttribute('data-state', state);

    /* Tekst-strings hieronder MOETEN exact matchen met de case-state in
       snippets/powdercoat-surcharge-info.liquid. */
    if (state === 'idle') {
      msgEl.innerHTML = '';
      barWrapEl.hidden = true;
    } else if (state === 'progress') {
      msgEl.innerHTML = 'Momenteel heb je <strong>' + totalDisplay + ' meter ' + escapeHtml(colorLabel) +
        '</strong> in je winkelwagen. Voeg nog <strong>' + remainingDisplay +
        ' meter</strong> toe om de coatingkosten te laten vervallen.';
      barWrapEl.hidden = false;
      barFillEl.style.width = pct + '%';
    } else {
      msgEl.innerHTML = '';
      barWrapEl.hidden = false;
      barFillEl.style.width = '100%';
    }
  };

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ---------- Boot ---------- */

  function boot() {
    new PowdercoatSurcharge();
    /* Single stateless controller per page — it queries DOM lazily so the
       variant-selector replaceWith on variant change doesn't break it. */
    new InfoBoxController();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
